import { NbaTeamBreakdown } from './NbaTeamBreakdown';
import { useBetMode, legFor, proStats } from '../lib/betMode';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Sparkles, Ticket, ChevronDown } from 'lucide-react';
import Scorecard from './SuggestionsScorecard';
import LineCheck from './LineCheck';
import WhySafe, { propWhy } from './WhySafe';
import { fmtPT } from '../lib/ptTime';
import { GamesBySlot } from './GamesBySlot';
import { InjuryTag, isRuledOut } from './InjuryTag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sports-betting`;
const pct = (p) => `${Math.round(p * 100)}%`;
const tone = (p) => (p >= 0.8 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
  : p >= 0.65 ? 'bg-sky-500/15 text-sky-300 border-sky-500/30' : 'bg-zinc-800 text-zinc-500 border-zinc-700');
export const legText = (x) => `${x.player} ${x.mark}+ ${x.label}${x.mark > 1 && x.stat !== 'passing_yards' && x.stat.endsWith('tds') ? 's' : ''}`;
const FILTERS = [['all', 'All'], ['passing_yards', 'QB pass yds'], ['passing_tds', 'QB pass TDs'], ['rushing_yards', 'Rush yds'], ['receiving_yards', 'Rec yds'], ['anytime_td', 'Anytime TD']];

const ago = (iso) => {
  if (!iso) return '—';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} hr ago` : `${Math.round(m / 1440)} days ago`;
};

const Logo = ({ src }) => (src ? <img src={src} alt="" className="w-5 h-5 rounded-full bg-white/90 p-[1px] object-contain shrink-0" /> : null);

const SHORT = { passing_yards: 'pass yds', rushing_yards: 'rush yds', receiving_yards: 'rec yds', passing_tds: 'pass TD', anytime_td: 'TD',
  points: 'pts', rebounds: 'reb', assists: 'ast', threes: '3PM', pra: 'pts+reb+ast', pr: 'pts+reb', pa: 'pts+ast', ra: 'reb+ast', steals: 'stl', blocks: 'blk', stocks: 'stl+blk' };
// Season + recent numbers for the pick's stat, and the last 3 box scores.
const PickStats = ({ x, testId, boxOnly = false }) => {
  const st = x.stats || {};
  const last = (x.recent || []).slice(-3).reverse();
  const extra = x.role === 'RB' ? (r) => `${r.carries} car` : x.role === 'QB' ? null : (r) => `${r.receptions}/${r.targets} tgt`;
  return (
    <div className="text-[10px] text-zinc-400 leading-snug" data-testid={`${testId}-stats`}>
      {!boxOnly && st.season_avg != null && <span>Season {st.season_avg} {SHORT[x.stat] || ''}/g · last 5 avg {st.last5_avg}{st.hits5 != null && st.last5?.length ? ` · cleared ${x.mark}+ in ${st.hits5}/${st.last5.length}` : ''}</span>}
      {last.length > 0 && (
        <span className="block text-zinc-500">
          {last.map((r) => `${r.home ? 'vs' : '@'} ${r.opp}: ${r[x.stat] ?? 0}${extra ? ` (${extra(r)})` : ''}`).join(' · ')}
        </span>
      )}
    </div>
  );
};

export const PickRow = ({ x, testId }) => {
  const mode = useBetMode();
  const pro = mode === 'pro';
  const st = x.stats || {};
  const n = (st.last5 || []).length;
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-zinc-800/70 last:border-0" data-testid={testId}>
      <Logo src={x.team_logo} />
      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold text-white ${pro ? 'truncate font-mono' : ''}`} data-testid={`${testId}-text`}>
          {x.depth && <span className="mr-1 rounded bg-zinc-700 px-1 align-middle text-[9px] font-bold text-white" data-testid={`${testId}-depth`}>{x.depth}</span>}
          {legFor(mode, x)}
          {x.injury && <span className="ml-1"><InjuryTag injury={x.injury} testId={`${testId}-injury`} withDetail /></span>}
          {x.lineup?.warning && <span className="ml-1 rounded bg-orange-500/25 px-1 align-middle text-[9px] font-bold text-orange-200" data-testid={`${testId}-lineup`}>⚠ {x.lineup.warning}</span>}
          {x.lowered && <span className="ml-1 rounded bg-amber-500/20 px-1 align-middle text-[9px] font-bold text-amber-200" title={x.lowered} data-testid={`${testId}-lowered`}>LINE LOWERED</span>}
        </p>
        {pro ? (
          <>
            <p className="text-[11px] text-zinc-400 font-mono">{proStats(x)}</p>
            <p className="text-[11px] text-zinc-500">{x.game}{x.cushion ? ` · ${Math.round(x.cushion)} under proj` : ''}{x.questionable ? ' · ⚠ Q' : ''}</p>
          </>
        ) : (
          <ul className="text-[12px] text-zinc-400 leading-snug">
            {st.season_avg != null && <li>Usually about <b className="text-zinc-200">{st.season_avg}</b> a game{st.last5_avg != null ? <>, <b className="text-zinc-200">{st.last5_avg}</b> lately</> : null}.</li>}
            {n > 0 && st.hits5 != null && <li>Did it in <b className="text-emerald-300">{st.hits5} of the last {n}</b> games.</li>}
            {x.projection != null && <li>Our guess tonight: about <b className="text-zinc-200">{x.projection}</b>.</li>}
            <li className="text-zinc-500">{x.game}{x.questionable ? ' · ⚠ has injury news' : ''}</li>
          </ul>
        )}
        {x.nbc && <p className="text-[11px] font-semibold text-orange-300" data-testid={`${testId}-nbc`}>⚠ NBC Sports {fmtPT(x.nbc.posted_at)}: {x.nbc.headline}</p>}
        {x.league !== 'nba' && <UsageLine role={x.role} avg={x.season_avg_usage} recent={x.recent} testId={`${testId}-usage`} />}
        {x.lock === true && <p className="text-[10px] font-semibold text-emerald-300" data-testid={`${testId}-lock`}>Passed every safety check</p>}
        {x.lock === false && <p className="text-[10px] text-amber-300" data-testid={`${testId}-lock`}>Not enough near-locks — added with caution: {x.lock_notes.join(', ')}</p>}
        {pro && <PickStats x={x} testId={testId} boxOnly />}
        <WhySafe lines={propWhy(x)} testId={`${testId}-why`} />
      </div>
      <span className={`shrink-0 rounded-md border px-1.5 py-0.5 font-mono text-xs font-bold ${tone(x.prob)}`}>{pct(x.prob)}{pro ? '' : ' likely'}</span>
    </div>
  );
};

const COMBO_STATS = new Set(['pra', 'pr', 'pa', 'ra']); // combined lines live in the tap-open detail only
const NBA_WORDS = { points: 'points', rebounds: 'rebounds', assists: 'assists', threes: '3-pointers', pra: 'pts + reb + ast', pr: 'pts + reb', pa: 'pts + ast', ra: 'reb + ast', steals: 'steals', blocks: 'blocks', stocks: 'steals + blocks' };
const WORDS = { passing_yards: 'passing yards', passing_tds: 'passing TDs', rushing_yards: 'rushing yards', receiving_yards: 'receiving yards', anytime_td: 'touchdowns', ...NBA_WORDS };
const expected = (pr) => (pr.stat.endsWith('yards') ? Math.round(pr.projection) : Math.round(pr.projection * 10) / 10);
const safeText = (pr) => (pr.stat.endsWith('yards') ? `${pr.best.mark}+` : NBA_WORDS[pr.stat] ? `${pr.best.mark}+ ${NBA_WORDS[pr.stat]}` : `${pr.best.mark}+ TD`);
const nick = (name) => String(name || '').split(' ').pop();

const COLS = {
  QB: [['cmp_att', 'C/Att'], ['passing_yards', 'Pass yds'], ['passing_tds', 'Pass TD'], ['interceptions', 'INT'], ['rushing_yards', 'Rush yds']],
  RB: [['carries', 'Carries'], ['rushing_yards', 'Rush yds'], ['receptions', 'Rec'], ['receiving_yards', 'Rec yds'], ['anytime_td', 'TD']],
  WR: [['targets', 'Targets'], ['receptions', 'Rec'], ['receiving_yards', 'Rec yds'], ['anytime_td', 'TD']],
  NBA: [['MIN', 'Min'], ['PTS', 'Pts'], ['REB', 'Reb'], ['AST', 'Ast'], ['3PT', '3PM']],
};

const cell = (r, k) => (k === 'cmp_att' ? `${r.completions ?? 0}/${r.pass_att ?? 0}` : r[k] ?? 0);

// Season totals, per-game averages and the last 3 box scores in one table.
const RecentGames = ({ p }) => {
  const s = p.season;
  if (!p.recent?.length && !s?.games) return null;
  const cols = p.league === 'nba' ? COLS.NBA : COLS[p.role] || COLS.WR;
  const row = (label, r, testId, cls = 'text-white') => (
    <tr key={label} className="border-t border-zinc-800/70" data-testid={testId}>
      <td className="py-0.5 text-zinc-400">{label}</td>
      {cols.map(([k]) => <td key={k} className={`text-right font-mono ${cls}`}>{cell(r, k)}</td>)}
    </tr>
  );
  return (
    <table className="w-full text-[11px]" data-testid={`sugg-recent-${p.player_id}`}>
      <thead><tr className="text-zinc-500"><th className="text-left font-normal">Stats</th>{cols.map(([, l]) => <th key={l} className="font-normal text-right">{l}</th>)}</tr></thead>
      <tbody>
        {s?.games > 0 && row(`${p.season_label || 'Season'} (${s.games} g)`, s.totals, `sugg-season-total-${p.player_id}`, 'text-amber-200 font-bold')}
        {s?.games > 0 && row('Per game', s.avg, `sugg-season-avg-${p.player_id}`, 'text-emerald-300')}
        {[...(p.recent || [])].reverse().map((r) => row(`${new Date(r.date).toLocaleDateString([], { month: 'numeric', day: 'numeric' })} ${r.home ? 'vs' : '@'} ${r.opp}`, r, `sugg-game-${p.player_id}-${r.date}`))}
      </tbody>
    </table>
  );
};

// How often an RB gets the ball (handoffs) or a WR/TE gets thrown to (targets): per game + last game.
const UsageLine = ({ role, avg, recent, testId }) => {
  const key = role === 'RB' ? 'carries' : role === 'WR' || role === 'TE' ? 'targets' : null;
  if (!key) return null;
  const last = (recent || []).slice(-1)[0];
  const per = avg?.[key];
  if (per == null && !last) return null;
  return (
    <p className="text-[12px] text-zinc-200 mt-0.5" data-testid={testId}>
      <span className="font-bold text-sky-300">{key === 'carries' ? 'Handoffs' : 'Targets'}:</span>{' '}
      <b>{per ?? '—'}</b> per game{last ? <> · <b>{last[key] ?? 0}</b> last game ({last.home ? 'vs' : '@'} {last.opp})</> : null}
    </p>
  );
};

// One-line season summary shown without opening the player.
const SEASON_LINE = {
  QB: [['passing_yards', 'pass yds'], ['passing_tds', 'pass TD'], ['rushing_yards', 'rush yds']],
  RB: [['rushing_yards', 'rush yds'], ['receiving_yards', 'rec yds'], ['anytime_td', 'TD']],
  WR: [['receptions', 'rec'], ['receiving_yards', 'rec yds'], ['anytime_td', 'TD']],
  NBA: [['PTS', 'pts'], ['REB', 'reb'], ['AST', 'ast'], ['3PT', '3PM']],
};
const SeasonLine = ({ p }) => {
  const s = p.season;
  if (!s) return null;
  if (!s.games) return <p className="text-[11px] text-zinc-500 mt-0.5" data-testid={`sugg-season-line-${p.player_id}`}>No games played this season</p>;
  return (
    <p className="text-[11px] text-zinc-400 mt-0.5" data-testid={`sugg-season-line-${p.player_id}`}>
      {p.league === 'nba'
        ? `${p.season_label || 'Season'} (${s.games} g${s.wins + s.losses ? `, team ${s.wins}-${s.losses}` : ''}): ${SEASON_LINE.NBA.map(([k, l]) => `${s.avg[k]} ${l}`).join(' · ')} per game`
        : `Season (${s.games} g): ${(SEASON_LINE[p.role] || SEASON_LINE.WR).map(([k, l]) => `${s.totals[k]} ${l} (${s.avg[k]}/g)`).join(' · ')}`}
    </p>
  );
};

// Simple line per stat; tap the player to see what the AI thinks + all the numbers.
const PlayerRow = ({ p, oppName, onTicket }) => {
  const [open, setOpen] = useState(false);
  const mode = useBetMode();
  return (
    <div className="border-b border-zinc-800/70 last:border-0" data-testid={`sugg-player-${p.player_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left py-2" data-testid={`sugg-player-toggle-${p.player_id}`}>
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-bold text-white">{p.name}</span>
          {p.depth && <span className="rounded bg-zinc-700 px-1 text-[9px] font-bold text-white" data-testid={`sugg-depth-${p.player_id}`}>{p.depth}</span>}
          <span className="text-[11px] text-zinc-500">({p.role}) vs {oppName}</span>
          {p.injury && <InjuryTag injury={p.injury} testId={`sugg-injury-tag-${p.player_id}`} />}
          {onTicket && <span className="rounded bg-sky-500 px-1 text-[9px] font-bold text-black" data-testid={`sugg-on-ticket-${p.player_id}`}>ON YOUR TICKET</span>}
          <ChevronDown className={`ml-auto w-4 h-4 text-zinc-500 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
        <SeasonLine p={p} />
        {p.league !== 'nba' && <UsageLine role={p.role} avg={p.season?.avg} recent={p.recent} testId={`sugg-usage-${p.player_id}`} />}
        {p.backup && <p className="text-[11px] text-zinc-500" data-testid={`sugg-backup-${p.player_id}`}>Backup QB — stats only, no pick</p>}
        {isRuledOut(p.injury) && <p className="text-[12px] font-semibold text-red-300" data-testid={`sugg-no-pick-${p.player_id}`}>Not playing tonight — no pick</p>}
        {!isRuledOut(p.injury) && p.props.filter((pr) => !COMBO_STATS.has(pr.stat)).map((pr) => (
          mode === 'pro' ? (
            <p key={pr.stat} className="text-[12px] text-zinc-300 mt-0.5 font-mono" data-testid={`sugg-pro-${p.player_id}-${pr.stat}`}>
              proj {expected(pr)} {SHORT[pr.stat]}{pr.best ? <> → <span className="font-bold text-emerald-300">{pr.stat === 'anytime_td' ? 'ATD' : `o${pr.best.mark - 0.5}`}</span> ({Math.round(pr.best.prob * 100)}%)</> : ' → pass'}
            </p>
          ) : <p key={pr.stat} className="text-[13px] text-zinc-300 mt-0.5">
            Expected: <span className="font-bold text-white">{expected(pr)} {expected(pr) === 1 ? WORDS[pr.stat].replace(/s$/, '').replace(/s \(/, ' (') : WORDS[pr.stat]}</span>
            {pr.best && <> → safe pick <span className="font-bold text-emerald-300">{safeText(pr)}</span>{pr.cushion ? <span className="text-[11px] text-zinc-500"> ({Math.round(pr.cushion)} under, in case something happens)</span> : null}</>}
            {!pr.best && pr.league !== 'nfl' && NBA_WORDS[pr.stat] && <span className="text-zinc-500"> → no safe pick tonight</span>}
          </p>
        ))}
      </button>
      {open && (
        <div className="mb-2 rounded-lg bg-zinc-900 border border-zinc-800 p-2 space-y-2" data-testid={`sugg-player-detail-${p.player_id}`}>
          <RecentGames p={p} />
          {p.injury && <p className="text-[11px] text-orange-300" data-testid={`sugg-injury-${p.player_id}`}>Injury: {p.injury.status}{p.injury.detail ? ` · ${p.injury.detail}` : ''}{p.injury.nbc ? ` (NBC Sports, ${fmtPT(p.injury.nbc.posted_at)})` : ''}</p>}
          {(p.news || []).slice(0, 2).map((n, i) => <p key={`${i}-${n.headline}`} className="text-[11px] text-sky-300">📰 {n.headline}</p>)}
          {p.props.map((pr) => (
            <div key={pr.stat}>
              <p className="text-[12px] font-semibold text-white">{WORDS[pr.stat][0].toUpperCase() + WORDS[pr.stat].slice(1)}</p>
              {pr.reason && <p className="text-[12px] text-amber-200"><span className="font-bold">AI says: </span>{pr.reason}</p>}
              <p className="text-[11px] text-zinc-400">
                Last 5 games: {pr.last5_avg} · Season: {pr.season_avg}
                {pr.opp_allows != null && <> · {oppName} allow {pr.opp_allows}/game (league avg {pr.league_avg_allowed})</>}
              </p>
              <div className="mt-1 flex flex-wrap gap-1">
                {pr.marks.map((m) => <span key={m.mark} className={`rounded border px-1.5 text-[10px] font-mono ${tone(m.prob)}`}>{m.mark}+ {pct(m.prob)}</span>)}
              </div>
              <LineCheck pr={pr} testId={`line-check-${p.player_id}-${pr.stat}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TeamSide = ({ team, opp, players, bench, eventId, ticketNames }) => (
  <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2" data-testid={`sugg-team-${eventId}-${team.abbr}`}>
    <div className="flex items-center gap-2">
      <Logo src={team.logo} />
      <span className="text-sm font-bold text-white">{team.name}</span>
    </div>
    {team.outlook && (
      <p className="mt-1 text-[11px] text-amber-100/90" data-testid={`sugg-outlook-${eventId}-${team.abbr}`}>
        <span className="font-bold text-amber-300">Game plan: </span>{`${team.outlook.split('. ')[0].replace(/\.$/, '')}.`}
      </p>
    )}
    {players.length ? players.map((p) => <PlayerRow key={p.player_id} p={p} oppName={nick(opp?.name)} onTicket={ticketNames?.has(String(p.name).toLowerCase())} />)
      : <p className="text-[11px] text-zinc-500">No players with enough stats yet.</p>}
    <NbaTeamBreakdown team={team} opp={opp} players={[...players, ...(bench || [])]} />
  </div>
);

export const GameCard = ({ g, badge, ticketNames, extra, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const teams = [...g.teams].sort((a) => (a.home_away === 'away' ? -1 : 1));
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/70" data-testid={`sugg-game-${g.event_id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 p-3 text-left" data-testid={`sugg-game-toggle-${g.event_id}`}>
        {teams.map((t) => <Logo key={t.id} src={t.logo} />)}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white truncate">{g.short_name}{badge}</p>
          <p className="text-[11px] text-zinc-500">{`${new Date(g.date).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} PT`}</p>
        </div>
        <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-black transition-transform ${open ? 'rotate-180' : ''}`}>
          <ChevronDown className="w-5 h-5" />
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2">
          {extra}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {teams.map((t) => (
              <TeamSide key={t.id} team={t} opp={teams.find((x) => x.id !== t.id)} eventId={g.event_id} ticketNames={ticketNames}
                players={g.players.filter((p) => p.team === t.abbr)} bench={(g.bench_players || []).filter((p) => p.team === t.abbr)} />
            ))}
          </div>
          {g.inactive?.length > 0 && (
            <details className="text-[11px] text-red-300" data-testid={`sugg-inactive-${g.event_id}`}>
              <summary className="cursor-pointer">Not playing — {g.inactive.length} out / doubtful / IR (tap to view)</summary>
              <p className="mt-1 text-red-300/80">{g.inactive.join(', ')}</p>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default function SuggestionsTab({ onTracked }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = () => axios.get(`${API}/suggestions`).then((r) => setData(r.data)).catch(() => setData({ error: 'Could not load suggestions' }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Switch to the next betting day right when it opens (Fri 11:59 PM, Mon 12 AM, Wed 11:59 AM PT).
    const at = data?.slate?.next_change ? new Date(data.slate.next_change).getTime() - Date.now() : null;
    if (at == null || at <= 0 || at > 7 * 864e5) return undefined;
    const t = setTimeout(load, at + 2000);
    return () => clearTimeout(t);
  }, [data?.slate?.next_change]);
  useEffect(() => {
    if (!data?.building) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [data]);

  const top = useMemo(() => (data?.top_picks || []).filter((x) => filter === 'all' || x.stat === filter), [data, filter]);
  const rebuild = async () => { await axios.post(`${API}/suggestions/rebuild`); setData((d) => ({ ...d, building: true })); };
  const track = async () => {
    const legs = data.suggested_ticket.map((x, i) => ({
      id: `s${i}`, league: 'nfl', bet_type: 'player_prop', player_name: x.player, stat_target: x.stat,
      selection: legText(x), line: `${x.mark}+`,
    }));
    try {
      await axios.post(`${API}/parlays`, { legs, leg_count: legs.length, descriptor: `Week ${data.week} suggested ticket` });
      toast.success('Suggested ticket added to the tracker');
      onTracked?.();
    } catch (e) { toast.error(`Could not add: ${e.response?.data?.detail || e.message}`); }
  };

  if (!data || (data.building && !data.games)) {
    return <div className="flex flex-col items-center gap-2 py-16 text-zinc-400" data-testid="sugg-loading"><Loader2 className="w-6 h-6 animate-spin" />Building this week's NFL suggestions…</div>;
  }
  return (
    <div className="space-y-4" data-testid="suggestions-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-400" />NFL Suggestions — Week {data.week}</h2>
          {data.slate && (
            <p className="text-[12px] font-semibold text-amber-300" data-testid="sugg-slate">
              Only {data.slate.label} games · switches to {data.slate.next_label}
            </p>
          )}
          <p className="text-[11px] text-zinc-400" data-testid="sugg-updated">
            Updated {ago(data.built_at)} · auto-updates every 2 hrs (every 30 min on game days), even when the app is closed
          </p>
          <p className="text-[10px] text-zinc-600">ESPN box scores, injury reports, rosters and NFL news. Stat-based probabilities, self-adjusted by past results; not sportsbook odds.</p>
        </div>
        <button type="button" onClick={rebuild} disabled={data.building} className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3 py-1.5 text-xs text-zinc-300 hover:text-white disabled:opacity-50" data-testid="sugg-rebuild">
          <RefreshCw className={`w-3.5 h-3.5 ${data.building ? 'animate-spin' : ''}`} />{data.building ? 'Updating…' : 'Rebuild now'}
        </button>
      </div>
      {data.error && <p className="text-xs text-red-400" data-testid="sugg-error">{data.error}</p>}
      {data.changes?.length > 0 && (
        <div className="rounded-xl border border-sky-500/30 bg-sky-950/20 p-3" data-testid="sugg-changes">
          <p className="text-xs font-bold text-sky-300 mb-1">What changed{data.changes_since ? ` since ${ago(data.changes_since)}` : ''}</p>
          <ul className="space-y-0.5">{data.changes.map((c) => <li key={c} className="text-[11px] text-zinc-300">• {c}</li>)}</ul>
        </div>
      )}

      <div className="space-y-2">
        {data.games && !data.games.length && <p className="text-xs text-zinc-400" data-testid="sugg-slate-empty">No NFL games on {data.slate?.label}. Next up: {data.slate?.next_label}.</p>}
        <p className="text-sm font-bold text-white">{data.slate ? `${data.slate.label} games` : 'Games this week'} <span className="font-normal text-[11px] text-zinc-500">· tap a game to open it, tap a player for the AI breakdown</span></p>
        <GamesBySlot games={data.games || []} testId="nfl-slots" render={(g) => <GameCard key={g.event_id} g={g} />} />
      </div>
      {data.suggested_ticket?.length > 0 && (
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-950/20 p-3" data-testid="sugg-ticket">
          <div className="flex items-center justify-between gap-2 mb-1">
            <p className="text-sm font-bold text-emerald-300">Suggested {data.suggested_ticket.length}-leg ticket</p>
            <button type="button" onClick={track} className="inline-flex items-center gap-1 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-bold text-black hover:bg-amber-400" data-testid="sugg-track-ticket">
              <Ticket className="w-3.5 h-3.5" />Track this ticket
            </button>
          </div>
          {data.suggested_ticket.map((x, i) => <PickRow key={`${x.player}-${x.stat}`} x={x} testId={`sugg-ticket-leg-${i}`} />)}
        </div>
      )}

      <div className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3" data-testid="sugg-top-picks">
        <p className="text-sm font-bold text-white mb-2">{data.slate ? `Best marks — ${data.slate.label}` : "Best marks this week"}</p>
        <div className="flex flex-wrap gap-1 mb-2">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`sugg-filter-${k}`}
              className={`rounded-full border px-2.5 py-0.5 text-[11px] ${filter === k ? 'bg-amber-500 text-black border-amber-500 font-semibold' : 'border-zinc-700 text-zinc-400'}`}>{label}</button>
          ))}
        </div>
        {top.length ? top.map((x, i) => <PickRow key={`${x.player}-${x.stat}`} x={x} testId={`sugg-top-${i}`} />) : <p className="text-xs text-zinc-500">No marks in this category clear the bar this week (75%, or 50% for anytime TD).</p>}
      </div>

      <Scorecard />
    </div>
  );
}
